import { afterNextRender, ApplicationRef, Component, inject, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { filter, firstValueFrom, Subscription, take } from 'rxjs';
import { WhatsappButton } from './shared/landing/widgets/whatsapp-button/whatsapp-button.js';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, WhatsappButton],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnDestroy {
  protected readonly title = signal('app');
  protected readonly loaderVisible = signal(true);
  protected readonly loaderLeaving = signal(false);
  protected readonly loaderStatus = signal('Loading page and imagery');

  private readonly router = inject(Router);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly subscriptions = new Subscription();
  private revealObserver?: IntersectionObserver;
  private contentObserver?: MutationObserver;
  private navigationVersion = 0;
  private currentPath = '';
  private reduceMotion = false;

  constructor(seo: SeoService) {
    seo.init();
    afterNextRender(() => {
      import('../assets/js/tabler.js');
      this.initializePageExperience();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revealObserver?.disconnect();
    this.contentObserver?.disconnect();
  }

  private initializePageExperience(): void {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.currentPath = this.pathFromUrl(this.router.url);
    document.body.classList.add('ece-page-loading');

    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          const nextPath = this.pathFromUrl(event.url);
          if (nextPath !== this.currentPath) {
            this.navigationVersion += 1;
            this.showLoader();
          }
        }

        if (event instanceof NavigationEnd) {
          const nextPath = this.pathFromUrl(event.urlAfterRedirects);
          const routeChanged = nextPath !== this.currentPath;
          this.currentPath = nextPath;
          if (routeChanged) window.scrollTo({ top: 0, behavior: 'auto' });
          if (routeChanged || this.loaderVisible()) {
            void this.completePageLoad(this.navigationVersion);
          }
        }
      })
    );

    void this.completePageLoad(this.navigationVersion);
  }

  private showLoader(): void {
    this.revealObserver?.disconnect();
    this.contentObserver?.disconnect();
    this.loaderStatus.set('Loading page and imagery');
    this.loaderLeaving.set(false);
    this.loaderVisible.set(true);
    document.body.classList.add('ece-page-loading');
  }

  private async completePageLoad(version: number): Promise<void> {
    const startedAt = performance.now();

    await firstValueFrom(this.applicationRef.isStable.pipe(filter(Boolean), take(1)));
    await this.nextPaint(2);

    if (version !== this.navigationVersion) return;

    await Promise.race([
      this.waitForVisualAssets(),
      this.delay(9000)
    ]);

    if (version !== this.navigationVersion) return;

    const minimumDuration = this.reduceMotion ? 120 : 650;
    const remainingDuration = Math.max(0, minimumDuration - (performance.now() - startedAt));
    if (remainingDuration) await this.delay(remainingDuration);

    if (version !== this.navigationVersion) return;

    this.prepareRevealElements(document);
    this.loaderStatus.set('Everything is ready');
    this.loaderLeaving.set(true);

    await this.delay(this.reduceMotion ? 80 : 480);
    if (version !== this.navigationVersion) return;

    this.loaderVisible.set(false);
    this.loaderLeaving.set(false);
    document.body.classList.remove('ece-page-loading');
    await this.nextPaint();
    this.startRevealObservers();
  }

  private async waitForVisualAssets(): Promise<void> {
    const assetUrls = new Set<string>();

    document.querySelectorAll<HTMLImageElement>('app-root img').forEach((image) => {
      const source = image.currentSrc || image.src;
      if (source) assetUrls.add(source);
    });

    document.querySelectorAll<HTMLElement>('app-root *').forEach((element) => {
      const background = getComputedStyle(element).backgroundImage;
      for (const match of background.matchAll(/url\(["']?(.*?)["']?\)/g)) {
        if (match[1]) assetUrls.add(match[1]);
      }
    });

    const imagePromises = [...assetUrls].map((url) => this.preloadImage(url));
    const videoPromises = [...document.querySelectorAll<HTMLVideoElement>('app-root video.video-active')]
      .map((video) => this.waitForVideo(video));

    const fontsReady = 'fonts' in document ? document.fonts.ready.then(() => undefined) : Promise.resolve();
    await Promise.allSettled([fontsReady, ...imagePromises, ...videoPromises]);
  }

  private preloadImage(url: string): Promise<void> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = url;
      if (image.complete) resolve();
    });
  }

  private waitForVideo(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve();

    return new Promise((resolve) => {
      const finish = () => {
        video.removeEventListener('loadeddata', finish);
        video.removeEventListener('error', finish);
        resolve();
      };
      video.addEventListener('loadeddata', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
    });
  }

  private prepareRevealElements(root: ParentNode): HTMLElement[] {
    const sectionSelector = [
      'section',
      'header.video-jumbotron',
      'article',
      'footer'
    ].join(',');
    const rootElement = root instanceof HTMLElement && root.matches(sectionSelector) ? [root] : [];
    const sections = [...rootElement, ...root.querySelectorAll<HTMLElement>(sectionSelector)]
      .filter((element) => element.closest('app-root') && !element.closest('.page-loader') && !element.classList.contains('ece-reveal'));

    sections.forEach((section) => section.classList.add('ece-reveal'));

    sections.forEach((section, sectionIndex) => {
      section.dataset['revealDirection'] = sectionIndex % 3 === 1 ? 'left' : sectionIndex % 3 === 2 ? 'right' : 'up';

      const children = section.querySelectorAll<HTMLElement>([
        '.card',
        '.grid-tile',
        '.service-item',
        '.story-marquee',
        '.founder-profile',
        '.quality-signal',
        '.capability-info-grid > *',
        '.row > [class*="col-"]'
      ].join(','));

      [...children].slice(0, 12).forEach((child, childIndex) => {
        if (child.closest('.ece-reveal') !== section) return;
        child.classList.add('ece-reveal-child');
        child.style.setProperty('--ece-reveal-delay', `${Math.min(childIndex, 7) * 70}ms`);
      });

      const bounds = section.getBoundingClientRect();
      if (bounds.top < window.innerHeight * .92 && bounds.bottom > 0) {
        this.revealSection(section);
      }
    });

    return sections;
  }

  private startRevealObservers(): void {
    this.revealObserver?.disconnect();
    this.contentObserver?.disconnect();

    if (this.reduceMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll<HTMLElement>('.ece-reveal').forEach((element) => element.classList.add('is-visible'));
      document.querySelectorAll<HTMLElement>('.ece-reveal-child').forEach((element) => element.classList.add('is-visible'));
      return;
    }

    this.revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const section = entry.target as HTMLElement;
        this.revealSection(section);
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });

    document.querySelectorAll<HTMLElement>('.ece-reveal:not(.is-visible)')
      .forEach((element) => this.revealObserver?.observe(element));

    this.contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          this.prepareRevealElements(node).forEach((element) => {
            if (!element.classList.contains('is-visible')) this.revealObserver?.observe(element);
          });
        });
      });
    });
    this.contentObserver.observe(document.querySelector('app-root') ?? document.body, { childList: true, subtree: true });
  }

  private pathFromUrl(url: string): string {
    return url.split(/[?#]/, 1)[0] || '/';
  }

  private revealSection(section: HTMLElement): void {
    section.classList.add('is-visible');
    section.querySelectorAll<HTMLElement>('.ece-reveal-child').forEach((child) => {
      if (child.closest('.ece-reveal') === section) child.classList.add('is-visible');
    });
  }

  private nextPaint(frames = 1): Promise<void> {
    return new Promise((resolve) => {
      const advance = (remaining: number) => requestAnimationFrame(() => remaining > 1 ? advance(remaining - 1) : resolve());
      advance(frames);
    });
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }
}
